import { useContext, useState } from "react";
import { Megaphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import CreateSendModal from "@/components/broadcast/CreateSendModal";
import MerchantContext from "@/contexts/MerchantContext";
import posthog from "@/lib/posthog";
import { useInactivoSelection } from "../InactivoSelectionContext";

const FloatingActionBar = ({ onCreate }: { onCreate: () => void }) => {
  const selection = useInactivoSelection();
  const count = selection.ids.size;
  if (!selection.enabled || count === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-pill border border-neutral-300 bg-surface py-2 pl-4 pr-2 shadow-xl">
      <span className="text-sm font-medium text-neutral-900">
        {count} seleccionado{count > 1 ? "s" : ""}
      </span>
      <div className="h-5 w-px bg-neutral-300" />
      <Button size="sm" variant="ghost" className="gap-1.5 rounded-pill" onClick={onCreate}>
        <Megaphone className="h-4 w-4" /> Reactivar campaña
      </Button>
      <div className="h-5 w-px bg-neutral-300" />
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 rounded-full p-0"
        onClick={() => selection.clear()}
        aria-label="Limpiar selección"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Freezes the audience at click time so it survives selection changes while
// the modal is open; the modal only mounts (and fires its queries) when set.
export const InactivoBroadcastBridge = () => {
  const selection = useInactivoSelection();
  const { merchantId } = useContext(MerchantContext);
  const [snapshot, setSnapshot] = useState<{ leadIds: string[]; label: string } | null>(null);

  const handleCreate = () => {
    if (!selection.scope || selection.ids.size === 0) return;
    setSnapshot({
      leadIds: Array.from(selection.ids),
      label: selection.scope.label,
    });
    posthog.capture("tablero broadcast launched", {
      merchant_id: merchantId,
      lead_count: selection.ids.size,
      lane_id: selection.scope.id,
      select_all_used: selection.selectAllUsed,
      cap_hit: selection.capHit,
    });
  };

  return (
    <>
      <FloatingActionBar onCreate={handleCreate} />
      {snapshot ? (
        <CreateSendModal
          isOpen
          onClose={() => setSnapshot(null)}
          onSuccess={() => {
            selection.clear();
            setSnapshot(null);
          }}
          initialLeadIds={snapshot.leadIds}
          initialLeadIdsLabel={snapshot.label}
        />
      ) : null}
    </>
  );
};

export default FloatingActionBar;
